import { createContext, useContext } from 'react';

// The page-level filters (date window + single-product toggle) shared with both
// tabs THROUGH CONTEXT rather than props.
//
// Why context: DTabs renders each tab as `<Content />` where `content` is the
// closure the page hands it. If that closure captured fromDate/toDate/
// singleProductOnly, it would be a new function every time one of them changed,
// React would see a new component type and REMOUNT the tab — throwing away the
// machine/product the user had selected. Passing the filters via context keeps
// the content closures stable (they depend only on `machines`), so a filter
// change re-renders the tab in place and the selection survives.
export interface PerfFilters {
    fromDate: string;
    toDate: string | null;
    singleProductOnly: boolean;
}

const PerfFiltersContext = createContext<PerfFilters>({
    fromDate: '',
    toDate: null,
    singleProductOnly: false,
});

export const PerfFiltersProvider = PerfFiltersContext.Provider;

export function usePerfFilters(): PerfFilters {
    return useContext(PerfFiltersContext);
}

// A focused corrida, shared within the Máquinas/Productos analysis panel so the
// scatter can highlight the point the user clicked. It used to also drive the
// monthly audit table that sat below the tabs; that table is now its own
// "Auditoría semanal" tab and no longer reads a selection, so only the scatter's
// own click→highlight remains — but the plumbing stays page-level because the
// emitter (the Gráfica scatter) and the reader (the panel) are separate nodes.
export type PerfRunFocusSource = 'graph' | 'audit';

export interface PerfRunFocusRequest {
    orderProductionId: number;
    monthKey: string;
    source: PerfRunFocusSource;
}

export interface PerfRunFocus extends PerfRunFocusRequest {
    // Changes even when the same graph point is clicked twice, so a reader can
    // repeat its scroll/focus action.
    requestId: number;
}

interface PerfFocusContextValue {
    runFocus: PerfRunFocus | null;
    focusRun: (run: PerfRunFocusRequest) => void;
}

const PerfFocusContext = createContext<PerfFocusContextValue>({
    runFocus: null,
    focusRun: () => undefined,
});

export const PerfFocusProvider = PerfFocusContext.Provider;

export function usePerfRunFocus(): PerfRunFocus | null {
    return useContext(PerfFocusContext).runFocus;
}

export function useFocusPerfRun(): (run: PerfRunFocusRequest) => void {
    return useContext(PerfFocusContext).focusRun;
}
